import { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding } from "@fortawesome/free-solid-svg-icons";
import DataTable from "../../core/DataTable.jsx";
import { Link } from "react-router-dom";

import Loading from "../../layout/Loading.jsx";
import Button from "../../core/Button.jsx";
import Badge from "../../core/Badge.jsx";
import SearchInput from "../../input/SearchInput.jsx";
import useTriggeredAPICall from "../../../hooks/fetch-hooks/useTriggeredAPICall.js";
import usePagination from "../../../hooks/usePagination.js";
import useSearchTerm from "../../../hooks/useSearchTerm.js";
import useDebounce from "../../../hooks/useDebounce.js";
import useDelayedLoading from "../../../hooks/useDelayedLoading.js";
import { formatPhone } from "../../../util/stringUtils";

const companiesEndpoint = "/companies?limit=10&page=1&include=contacts";

const CompaniesListPage = () => {
  const [searchTerm, setSearchTerm] = useSearchTerm();
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const history = useHistory();
  const [sortParams, setSortParams] = useState(() => {
    const queryParams = new URLSearchParams(history.location.search);
    return {
      sortBy: queryParams.get("sort") || "",
      sortDir: queryParams.get("sort_dir") || "asc",
    };
  });
  const searchDebounce = useDebounce(searchTerm);

  const { fetchData, data, isLoading } = useTriggeredAPICall();
  const showLoading = useDelayedLoading(isLoading);
  const handleSelectedRowsChange = useCallback(({ selectedRows }) => {
    setSelectedCompanies(selectedRows);
  }, []);

  const baseUrl = "/companies";
  const { currentData, recordsData, listPage } = usePagination(
    data,
    baseUrl,
    fetchData
  );

  const getPrimaryContact = (company) => {
    if (!company.contacts || company.contacts.length === 0) return "-";
    const primaryContact = company.contacts.find((c) => c.role === "Primary");
    if (!primaryContact) return "-";
    return `${primaryContact.first_name} ${primaryContact.last_name}`;
  };

  const getBillingContact = (company) => {
    if (!company.contacts || company.contacts.length === 0) return "-";
    const billingContact = company.contacts.find((c) => c.role === "Billing");
    if (!billingContact) return "-";
    return `${billingContact.first_name} ${billingContact.last_name}`;
  };

  const getCityState = (company) => {
    if (!company.city && !company.state) return "-";
    if (!company.city) return company.state;
    if (!company.state) return company.city;
    return `${company.city}, ${company.state}`;
  };

  const columns = [
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
      width: "154px",
      cell: (row) => (
        <Link
          className="table-link truncate"
          to={{ pathname: `/company/${row.company_id}` }}
        >
          {row.name}
        </Link>
      ),
    },
    {
      name: "Organization",
      selector: (row) => row.organization?.name || "-",
      sortable: true,
      width: "140px",
      cell: (row) => (
        <div className="text-primary truncate">
          {row.organization?.name || "-"}
        </div>
      ),
    },
    {
      name: "City, State",
      selector: (row) => getCityState(row),
      sortable: true,
      width: "98px",
      cell: (row) => getCityState(row),
    },
    {
      name: "Primary Contact",
      selector: (row) => getPrimaryContact(row),
      sortable: true,
      width: "120px",
      cell: (row) => getPrimaryContact(row),
    },
    {
      name: "Billing Contact",
      selector: (row) => getBillingContact(row),
      sortable: true,
      width: "120px",
      cell: (row) => getBillingContact(row),
    },
    {
      name: "Phone",
      selector: (row) => row.phone || "-",
      sortable: true,
      width: "15%",
      minWidth: "105px",
      cell: (row) => (row.phone ? formatPhone(row.phone) : "-"),
    },
    {
      name: "Email",
      selector: (row) => row.email || "-",
      sortable: true,
      width: "110px",
      cell: (row) => (
        <div className="text-primary truncate">
          {!row?.email ? "-" : row.email}
        </div>
      ),
    },
    {
      name: "Active",
      selector: (row) => row.is_active,
      sortable: true,
      width: "80px",
      cell: (row) => <Badge status={row.is_active} />,
    },
  ];

  useEffect(() => {
    const params = new URLSearchParams(history.location.search);

    if (sortParams.sortBy) {
      params.set("sort", sortParams.sortBy);
      params.set("sort_dir", sortParams.sortDir);
    } else {
      params.delete("sort");
      params.delete("sort_dir");
    }

    history.replace({
      pathname: history.location.pathname,
      search: params.toString(),
    });
  }, [sortParams, history]);

  useEffect(() => {
    const url = searchDebounce
      ? `/companies/search?q=${searchDebounce}&include=contacts`
      : companiesEndpoint;

    fetchData(url);
  }, [searchDebounce, fetchData]);

  if (showLoading && !data) {
    return <Loading />;
  }

  if (!currentData) {
    return null;
  }

  return (
    <div className="flex-align-start width-full">
      <h1>
        <FontAwesomeIcon icon={faBuilding} className="companies-icon" />{" "}
        Companies
      </h1>
      <div className="flex-space-between">
        <SearchInput
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="width-200px"
        />
        <Button to="/company-form">+ New Company</Button>
      </div>

      <Loading isLoading={showLoading}>
        <DataTable
          columns={columns}
          data={currentData}
          loading={showLoading}
          onRowClick={(row) => history.push(`/company/${row.company_id}`)}
          pointerOnHover
          highlightOnHover
          selectableRows
          onSelectedRowsChange={handleSelectedRowsChange}
          onPageChange={listPage}
          recordsData={recordsData}
          baseUrl={baseUrl}
          setSortParams={setSortParams}
          sortParams={sortParams}
          defaultSortFieldId={sortParams?.sortBy}
          defaultSortAsc={sortParams?.sortDir !== "desc"}
        />
      </Loading>
    </div>
  );
};

export default CompaniesListPage;
